import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getEmailStatus, sendEmail } from '../services/email';
import AnimatedSection from './AnimatedSection';
import { useAuth } from '../context/AuthContext';

const EmailSender: React.FC = () => {
  const { admin } = useAuth();

  // Sending identity comes from the admin's saved account details.
  const zohoEmail = admin?.zohoEmail || admin?.email || '';
  const senderName = admin?.name || '';
  const senderTitle = admin?.title || '';
  const senderPhone = admin?.phone || '';
  const hasSavedPassword = Boolean(admin?.hasSavedMailPassword);

  // Only needed when nothing is saved on the server yet.
  const [zohoPassword, setZohoPassword] = useState('');
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [smtpHost, setSmtpHost] = useState('smtppro.zoho.com:465');

  useEffect(() => {
    getEmailStatus()
      .then((data) => setSmtpHost(`${data.host}:${data.port}`))
      .catch(() => undefined);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!zohoEmail) {
      setError('No Zoho mailbox on your account. Set one in Account settings.');
      return;
    }

    if (!hasSavedPassword && !zohoPassword) {
      setError('Enter your Zoho password, or save it once in Account settings.');
      return;
    }

    if (!to.trim() || !subject.trim() || !message.trim()) {
      setError('To, subject, and message are required.');
      return;
    }

    try {
      setSending(true);
      const result = await sendEmail({
        // Omitted when a password is saved server-side.
        ...(zohoPassword ? { zohoEmail, zohoPassword } : {}),
        to: to.trim(),
        cc: cc.trim() || undefined,
        recipientName: recipientName.trim() || undefined,
        subject: subject.trim(),
        message: message.trim(),
        senderName: senderName.trim() || undefined,
        senderTitle: senderTitle.trim() || undefined,
        senderPhone: senderPhone.trim() || undefined,
      });

      setSuccess(result.message || 'Email sent successfully.');
      setZohoPassword('');
      setTo('');
      setCc('');
      setRecipientName('');
      setSubject('');
      setMessage('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="py-10 md:py-14">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection animation="slide-up">
          <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h1 className="font-display text-3xl md:text-4xl font-semibold text-white mb-2">
                Send Email
              </h1>
              <p className="text-muted">
                Each staff member signs in with their own Zoho mailbox to send.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/admin/email/bulk"
                className="text-sm px-3 py-2 rounded-lg border border-surface-border text-muted hover:text-white hover:border-primary-500/40 transition-colors"
              >
                Bulk email
              </Link>
              <div className="text-sm px-3 py-2 rounded-lg border border-surface-border text-muted">
                SMTP · {smtpHost}
              </div>
            </div>
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <AnimatedSection animation="slide-up" className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="space-y-5 rounded-2xl border border-surface-border bg-surface-card p-6 md:p-8">
              {error && (
                <div className="rounded-lg border border-red-500/40 bg-red-500/10 text-red-400 px-4 py-3 text-sm">
                  {error}
                </div>
              )}
              {success && (
                <div className="rounded-lg border border-primary-500/40 bg-primary-500/10 text-primary-400 px-4 py-3 text-sm">
                  {success}
                </div>
              )}

              <div className="rounded-xl border border-surface-border bg-surface p-4 space-y-4">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-white">Sending as {senderName || 'you'}</p>
                    <p className="text-xs text-primary-400 mt-1 truncate">{zohoEmail}</p>
                  </div>
                  <Link
                    to="/admin/account"
                    className="text-xs px-3 py-1.5 rounded-lg border border-surface-border text-muted hover:text-white hover:border-primary-500/40 transition-colors"
                  >
                    Edit details
                  </Link>
                </div>

                {hasSavedPassword ? (
                  <p className="text-xs text-primary-400">
                    ✓ Mail password saved — you will not be asked for it.
                  </p>
                ) : (
                  <div>
                    <label className="block text-sm text-muted mb-2">Zoho password *</label>
                    <input
                      type="password"
                      value={zohoPassword}
                      onChange={(e) => setZohoPassword(e.target.value)}
                      placeholder="Password or app password"
                      autoComplete="current-password"
                      className="w-full rounded-lg border border-surface-border bg-surface-card px-4 py-3 text-white placeholder:text-muted/50 focus:outline-none focus:border-primary-500"
                    />
                    <p className="text-xs text-muted mt-2">
                      Needed for this send only.{' '}
                      <Link to="/admin/account" className="text-primary-500 hover:text-primary-400">
                        Save it in Account
                      </Link>{' '}
                      to stop retyping it.
                    </p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-sm text-muted mb-2">To *</label>
                  <input
                    type="text"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                    placeholder="client@example.com"
                    className="w-full rounded-lg border border-surface-border bg-surface px-4 py-3 text-white placeholder:text-muted/50 focus:outline-none focus:border-primary-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm text-muted mb-2">Recipient name</label>
                  <input
                    type="text"
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                    placeholder="Optional greeting name"
                    className="w-full rounded-lg border border-surface-border bg-surface px-4 py-3 text-white placeholder:text-muted/50 focus:outline-none focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm text-muted mb-2">CC</label>
                  <input
                    type="text"
                    value={cc}
                    onChange={(e) => setCc(e.target.value)}
                    placeholder="optional@example.com"
                    className="w-full rounded-lg border border-surface-border bg-surface px-4 py-3 text-white placeholder:text-muted/50 focus:outline-none focus:border-primary-500"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm text-muted mb-2">Subject *</label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Email subject"
                    className="w-full rounded-lg border border-surface-border bg-surface px-4 py-3 text-white placeholder:text-muted/50 focus:outline-none focus:border-primary-500"
                    required
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm text-muted mb-2">Message *</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Write your message..."
                    rows={10}
                    className="w-full rounded-lg border border-surface-border bg-surface px-4 py-3 text-white placeholder:text-muted/50 focus:outline-none focus:border-primary-500 resize-y"
                    required
                  />
                </div>

              </div>

              <button
                type="submit"
                disabled={sending}
                className="btn-primary w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {sending ? 'Sending...' : 'Send Email'}
              </button>
            </form>
          </AnimatedSection>

          <AnimatedSection animation="slide-up" delay={100} className="lg:col-span-2">
            <div className="rounded-2xl border border-surface-border bg-white p-5 sticky top-28">
              <p className="text-xs uppercase tracking-widest text-zinc-400 mb-4 font-sans">Signature preview</p>

              <div className="flex gap-4 items-start border-t border-zinc-200 pt-4">
                <div className="shrink-0 w-[120px]">
                  <img src="/nexalogo.png" alt="Nexasolutra" className="w-10 h-10 mb-2 object-contain" />
                  <p className="font-semibold text-zinc-900 text-base leading-tight">Nexasolutra</p>
                  <p className="text-[9px] font-semibold text-primary-600 uppercase tracking-wide leading-snug mt-1.5">
                    AI Operating Systems for Hospitality & Commerce
                  </p>
                </div>

                <div className="w-0.5 self-stretch bg-primary-500 shrink-0" />

                <div className="min-w-0 pt-0.5">
                  <p className="font-serif font-bold text-zinc-900 text-lg leading-tight">
                    {senderName || 'Your Name'}
                  </p>
                  <p className="font-serif text-sm text-zinc-600 mt-0.5">{senderTitle || 'Your Title'}</p>
                  <p className="font-serif text-sm text-zinc-600 mb-3">Nexasolutra</p>
                  <p className="text-xs text-blue-600 mb-1 truncate">
                    {zohoEmail || 'you@nexasolutra.com'}
                  </p>
                  {senderPhone && <p className="text-xs text-zinc-800">{senderPhone}</p>}
                </div>
              </div>

              <div className="mt-5 pt-4 border-t border-zinc-200 space-y-2 text-xs text-zinc-600">
                <p>Flt 7 by Chief/Pal, Along Gbuduwyi Kabusa, Abuja</p>
                <p>Nexasolutra</p>
                <p className="text-blue-600">www.nexasolutra.com</p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

export default EmailSender;
